import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Import.css";

// The live instance has 307 assignable users, so this is a type-ahead (same
// searchable pattern as AssociationSelect / FieldSelect) rather than a <select>.
const MAX_VISIBLE = 50;

export function userDisplayName(user) {
	if (!user) return "";
	return String(user.full_name || user.username || user.email || `User ${user.id}`);
}

// "Full Name — username", the row/summary label.
export function userDisplayLabel(user) {
	if (!user) return "";
	const name = userDisplayName(user);
	const username = String(user.username || "");
	return username && username !== name ? `${name} — ${username}` : name;
}

// Searchable "assign the imported leads to this Iris user" picker. Default is
// unselected = NO assignment: the payload then carries no `users` key at all and
// Iris's own default behavior is untouched.
export default function AssignedUserSelect({ users, value, onChange }) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [highlight, setHighlight] = useState(0);
	const wrapRef = useRef(null);

	const selected = useMemo(
		() =>
			value === null || value === undefined || value === ""
				? null
				: (users || []).find((u) => String(u.id) === String(value)) || null,
		[users, value]
	);

	// Close on outside click, discarding any unmatched query.
	useEffect(() => {
		if (!open) return undefined;
		const onDown = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
				setQuery("");
			}
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return users || [];
		return (users || []).filter((u) =>
			`${u.full_name || ""} ${u.username || ""} ${u.email || ""}`
				.toLowerCase()
				.includes(q)
		);
	}, [users, query]);

	const visible = filtered.slice(0, MAX_VISIBLE);

	useEffect(() => {
		setHighlight(0);
	}, [query, open]);

	const noMatch = open && query.trim() !== "" && filtered.length === 0;

	const pick = (user) => {
		onChange(user ? user.id : null);
		setQuery("");
		setOpen(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setOpen(true);
			setHighlight((h) => Math.min(h + 1, Math.max(visible.length - 1, 0)));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlight((h) => Math.max(h - 1, 0));
		} else if (e.key === "Enter") {
			// Only ever commits a real user from the list.
			e.preventDefault();
			if (visible[highlight]) pick(visible[highlight]);
		} else if (e.key === "Escape") {
			setOpen(false);
			setQuery("");
		}
	};

	const selectedText = selected ? userDisplayLabel(selected) : "";

	return (
		<span className="assocSelect" ref={wrapRef}>
			<span className="assocInputRow">
				<input
					className={`importInput ${noMatch ? "noMatch" : ""}`}
					placeholder={selectedText || "None — search users by name, username or email…"}
					value={open ? query : selectedText}
					onFocus={() => {
						setOpen(true);
						setQuery("");
					}}
					onChange={(e) => {
						setOpen(true);
						setQuery(e.target.value);
					}}
					onKeyDown={handleKeyDown}
				/>
				{selected && (
					<button
						type="button"
						className="importBtn secondary small"
						title="Clear — import without assigning the leads"
						onClick={() => pick(null)}>
						✕
					</button>
				)}
			</span>

			{open && (
				<div className="fieldSelectMenu">
					<div className="fieldSelectList">
						<div
							className={`fieldSelectOption ${highlight === -1 ? "selected" : ""}`}
							onMouseEnter={() => setHighlight(-1)}
							onClick={() => pick(null)}>
							<span className="fieldSelectLabel">None (no assignment)</span>
							{!selected && <span className="fieldSelectMeta">selected</span>}
						</div>
						{visible.map((user, i) => (
							<div
								key={String(user.id)}
								className={`fieldSelectOption ${i === highlight ? "selected" : ""}`}
								onMouseEnter={() => setHighlight(i)}
								onClick={() => pick(user)}>
								<span className="fieldSelectLabel">{userDisplayLabel(user)}</span>
								{selected && String(selected.id) === String(user.id) && (
									<span className="fieldSelectMeta">selected</span>
								)}
							</div>
						))}
						{noMatch && (
							<div className="fieldSelectEmpty">No matching Iris user.</div>
						)}
						{filtered.length > MAX_VISIBLE && (
							<div className="fieldSelectEmpty">
								…{filtered.length - MAX_VISIBLE} more — keep typing to narrow.
							</div>
						)}
					</div>
				</div>
			)}
		</span>
	);
}
